/**
 * Ucitavanje tracking skripti uz GDPR usklađen Google Consent Mode v2.
 *
 * Google Analytics:
 *   - gtag.js se ucitava odmah sa podrazumevanim consent-om "denied"
 *   - salju se cookieless pingovi za agregatno modeliranje (bez GA kolacica)
 *   - nakon opt-in-a za "analytics" kategoriju, consent se azurira na "granted"
 *
 * Meta Pixel:
 *   - i dalje opt-in — ucitava se TEK nakon marketing saglasnosti
 *
 * Sve funkcije su idempotentne (guard preko window.__bpConsentLoaded).
 */

import { ConsentCategory } from './consentCategories';
import { CookieConsent } from './cookieConsentApi';

const GA4_MEASUREMENT_ID = 'G-JXY7ZBZV8R';
const META_PIXEL_ID = '1514524043603953';

function getGuards(): NonNullable<Window['__bpConsentLoaded']> {
    if (!window.__bpConsentLoaded) {
        window.__bpConsentLoaded = {};
    }
    return window.__bpConsentLoaded;
}

function ensureGtag(): void {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
        window.gtag = function gtag(...args: unknown[]) {
            window.dataLayer!.push(args);
        };
    }
}

/**
 * Inicijalizuje GA4 sa Consent Mode v2 (podrazumevano denied).
 * Pozvati jednom pri startu aplikacije — pre CookieConsent.run().
 */
export function initGoogleAnalyticsWithConsentMode(): void {
    const guards = getGuards();
    ensureGtag();

    if (!guards.consentDefaultSet) {
        guards.consentDefaultSet = true;
        window.gtag!('consent', 'default', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            wait_for_update: 500,
        });
    }

    if (guards.ga4) return;
    guards.ga4 = true;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    window.gtag!('js', new Date());
    window.gtag!('config', GA4_MEASUREMENT_ID, {
        anonymize_ip: true,
        send_page_view: false,
    });
}

/**
 * Sinhronizuje Google Consent Mode sa trenutnim izborom korisnika.
 */
export function syncGoogleConsent(options: {
    analytics: boolean;
    marketing: boolean;
}): void {
    ensureGtag();
    window.gtag!('consent', 'update', {
        analytics_storage: options.analytics ? 'granted' : 'denied',
        ad_storage: options.marketing ? 'granted' : 'denied',
        ad_user_data: options.marketing ? 'granted' : 'denied',
        ad_personalization: options.marketing ? 'granted' : 'denied',
    });
}

/** @deprecated Koristi initGoogleAnalyticsWithConsentMode + syncGoogleConsent */
export function loadGoogleAnalytics(): void {
    initGoogleAnalyticsWithConsentMode();
    syncGoogleConsent({ analytics: true, marketing: false });
}

/**
 * Ucitava Meta Pixel (fbevents.js) i registruje pocetni PageView.
 * Pozvati SAMO ako je consent kategorija "marketing" prihvacena.
 */
export function loadMetaPixel(): void {
    const guards = getGuards();
    if (guards.metaPixel) return;
    guards.metaPixel = true;

    (function (f: Window, b: Document, e: 'script', v: string) {
        if (f.fbq) return;
        const n: Fbq = function fbq(...args: unknown[]) {
            if (n.callMethod) {
                n.callMethod.apply(n, args);
            } else {
                n.queue!.push(args);
            }
        };
        f.fbq = n;
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const t = b.createElement(e);
        t.async = true;
        t.src = v;
        const s = b.getElementsByTagName(e)[0];
        s.parentNode!.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq!('init', META_PIXEL_ID);
    window.fbq!('track', 'PageView');
}

/**
 * Da li je korisnik prihvatio analytics kategoriju kolacica?
 * Koristi se kao guard pre slanja konverzionih eventa (npr. purchase).
 */
export function hasAnalyticsConsent(): boolean {
    try {
        return CookieConsent.acceptedCategory(ConsentCategory.ANALYTICS);
    } catch {
        return false;
    }
}

/**
 * Da li je consent za marketing trenutno dat?
 */
export function hasMarketingConsent(): boolean {
    try {
        return CookieConsent.acceptedCategory(ConsentCategory.MARKETING);
    } catch {
        return false;
    }
}

/**
 * Resetuje Meta Pixel guard kada korisnik povuce marketing consent.
 * GA guard se ne resetuje — gtag ostaje ucitan, consent se menja preko syncGoogleConsent().
 */
export function resetGuard(category: 'analytics' | 'marketing'): void {
    const guards = getGuards();
    if (category === 'marketing') guards.metaPixel = false;
}
